/* RESPONSIBLE TEAM: team-proactive-support */
import IntercomRoute from 'embercom/routes/base/intercom-route';
import { inject as service } from '@ember/service';
import { hash } from 'rsvp';
import {
  parsedESFilters,
  setControllerFilterProperties,
  filterQueryParams,
  setRouteFilterProperties,
} from 'embercom/lib/reporting/flexible/filter-helpers';
import ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';

const QUERY_PARAMS_ES_MAPPINGS = {
  teammateId: 'teammate_id',
  teamsParticipated: 'teammate_id', //a list of teammate ids is passed to ES
  scopingTagIds: 'conversation_tag_ids',
  channels: 'channel_type',
  continents: 'user_location.continent_code',
  countries: 'user_location.country_name',
  startedByUser: 'conversation_started_by_user',
  teammatesAssigned: 'admin_assignee_id',
  teamsAssigned: 'team_assignee_id',
  botInboxTime: null, //bot inbox is a setting, it's not passed to ES
  customAttributes: 'custom_attributes',
  topics: 'topic',
};

const FILTER_TYPES = Object.keys(QUERY_PARAMS_ES_MAPPINGS);

export default class ResponsivenessRoute extends IntercomRoute {
  @service appService;
  @service store;
  @service reportingService;
  @service intercomEventService;
  @service intl;
  @service customReportsService;
  analytics = { place: 'responsiveness' };
  queryParams = filterQueryParams(FILTER_TYPES);
  reportName = 'performance_responsiveness_report';
  reportContext = 'performance';

  dashboardIdentifier = 'inbox-performance-responsiveness';
  chartIdentifiers = [
    'response-time-counter',
    'first-response-time-counter',
    'time-to-close-counter',
    'response-time',
    'first-response-time',
    'time-to-close',
  ];

  shouldRedirectFromParent = true;

  get app() {
    return this.appService.app;
  }

  get range() {
    return this.reportingService.range;
  }

  get titleToken() {
    return this.intl.t('reporting.responsiveness.title');
  }

  filters() {
    let queryParamMappings = Object.assign({}, QUERY_PARAMS_ES_MAPPINGS);
    if (!this.app.canUseFeature('conversation_attributes')) {
      delete queryParamMappings.customAttributes;
    }
    if (!this.appService.app.canUseTopicFilter) {
      delete queryParamMappings.topics;
    }

    return parsedESFilters(this, queryParamMappings);
  }

  beforeModel(transition) {
    super.beforeModel(...arguments);

    if (this.appService.app.canSeeFullR2Experience) {
      this.customReportsService.routeToOverviewPage();
    }

    //setup filters
    let filters = FILTER_TYPES.filter((filter) => {
      if (filter === 'topics') {
        return this.appService.app.canUseTopicFilter;
      }

      return true;
    });
    setRouteFilterProperties(this, transition.to.queryParams, filters);
  }

  findDataConfig(dataConfigs, chartId) {
    let result = dataConfigs.findBy('chart_identifier', chartId);

    if (!result) {
      result = this.store.createRecord('reporting/chart', {
        dashboard_identifier: this.dashboardIdentifier,
        chart_identifier: chartId,
        aggregation_method: 'median',
        app_id: this.app.id,
      });
    }

    return result;
  }

  async model() {
    let reportingAllSettings = await this.store.queryRecord('reporting/settings', {});
    let botInboxSettings = await this.store.findRecord('operator-bot-inbox-settings', this.app.id);
    let conversationAttributeDescriptors = [];

    let fetchedDataConfigs = await this.store.query('reporting/chart', {
      dashboard_identifier: this.dashboardIdentifier,
    });

    let dataConfigs = this.chartIdentifiers.reduce((result, id) => {
      result[id] = this.findDataConfig(fetchedDataConfigs, id);
      return result;
    }, {});

    let settings = {
      reportingExcludeOooHours: reportingAllSettings.get('excludeOooHours'),
      allowToExcludeBotInbox: botInboxSettings.get('assignConversationsToBots'),
      excludeBotInboxStartDate: botInboxSettings.get('assignConversationsToBotsLastEnabledAt'),
      canUseTopicFilter: this.appService.app.canUseTopicFilter,
    };

    if (this.app.canUseFeature('conversation_attributes')) {
      conversationAttributeDescriptors =
        await ConversationAttributeDescriptor.peekAllAndMaybeLoad();
    }

    return hash({
      settings,
      dataConfigs,
      dashboardIdentifier: this.dashboardIdentifier,
      conversationAttributeDescriptors,
    });
  }

  setupController(controller, model) {
    super.setupController(...arguments);

    let { range, reportContext, reportName, tag } = this;
    let controllerProperties = {
      rangeStart: range.start,
      rangeEnd: range.end,
      range,
      rangeParams: {
        interval: this.range.interval,
        comparisonStartMoment: this.range.comparisonStartMoment,
        comparisonEndMoment: this.range.comparisonEndMoment,
        startMoment: this.range.startMoment,
        endMoment: this.range.endMoment,
        inDays: this.range.inDays,
        timezone: this.range.timezone,
      },
      reportContext,
      reportName,
      tag,
      ...model,
    };
    controller.setProperties(controllerProperties);
    setControllerFilterProperties(this, controller, FILTER_TYPES);
  }

  resetController(controller, isExiting, transition) {
    if (isExiting) {
      controller.clearReportSubscription();
    }
  }
}
